import React, { useState, useEffect } from 'react';
import { Calendar, AlertTriangle, Package, ChefHat, ShoppingCart, TrendingUp, Clock, CheckCircle, XCircle } from 'lucide-react';
import PageContainer from '../layout/PageContainer';

interface IngredientRequirement {
  ingredient_id: number;
  name: string;
  required_amount: number;
  unit: string;
  current_stock: number;
  shortage: number;
  status: 'sufficient' | 'warning' | 'insufficient';
  message: string;
}

interface BentoOrder {
  menu_name: string;
  quantity: number;
  pickup_time: string;
}

interface RequirementData {
  date: string;
  orders: BentoOrder[];
  total_orders: number;
  requirements: IngredientRequirement[];
  insufficient_count: number;
  warning_count: number;
}

interface PreparationSuggestion {
  ingredient_name: string;
  suggestion: string;
  prep_time: string;
  quantity: number;
  unit: string;
}

interface OrderListItem {
  ingredient_name: string;
  order_quantity: number;
  unit: string;
  supplier: string;
  estimated_cost: number;
  urgency: 'high' | 'medium' | 'low';
}

export const BentoInventoryIntegration: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [tomorrowData, setTomorrowData] = useState<RequirementData | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: string; end: string }>({
    start: new Date().toISOString().split('T')[0],
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [periodData, setPeriodData] = useState<RequirementData | null>(null);
  const [preparationSuggestions, setPreparationSuggestions] = useState<PreparationSuggestion[]>([]);
  const [orderList, setOrderList] = useState<OrderListItem[]>([]);
  const [activeTab, setActiveTab] = useState<'tomorrow' | 'period' | 'preparation' | 'order' | 'dailyStock'>('dailyStock');

  // 完成品在庫 state
  interface DailyStockItem {
    menuId: string;
    limitQty: number;
    remainingQty: number;
    updatedAt: string;
  }
  interface BentoMenuItem { id: number; name: string; price: number; isAvailable: number; }
  const [dailyStockItems, setDailyStockItems] = useState<DailyStockItem[]>([]);
  const [dailyStockDate, setDailyStockDate] = useState('');
  const [dailyStockLoading, setDailyStockLoading] = useState(false);
  const [dailyStockError, setDailyStockError] = useState('');
  const [bentoMenus, setBentoMenus] = useState<BentoMenuItem[]>([]);
  const [setMenuId, setSetMenuId] = useState('');
  const [setLimitQty, setSetLimitQty] = useState('');
  const [setRemainingQty, setSetRemainingQty] = useState('');
  const [setSubmitting, setSetSubmitting] = useState(false);
  const [adjustingMenuId, setAdjustingMenuId] = useState<string | null>(null);
  const [bulkSetQty, setBulkSetQty] = useState('');
  const [bulkSetSubmitting, setBulkSetSubmitting] = useState(false);

  useEffect(() => {
    fetchTomorrowRequirements();
    fetchDailyStock();
    fetchBentoMenus();
  }, []);

  const fetchBentoMenus = async () => {
    try {
      const response = await fetch('/api/bento-menu/products');
      const data = await response.json();
      if (Array.isArray(data)) {
        setBentoMenus(data.map((m: any) => ({ id: m.id, name: m.name, price: m.price, isAvailable: m.is_available })));
      }
    } catch (error) {
      // silent - dropdown will be empty
    }
  };

  const fetchTomorrowRequirements = async () => {
    try {
      const response = await fetch('/api/bento-inventory/tomorrow-requirements');
      const data = await response.json();
      setTomorrowData(data);

      // 在庫不足があれば自動で準備提案を取得
      if (data.insufficient_count > 0 || data.warning_count > 0) {
        fetchPreparationSuggestions();
      }
    } catch (error) {
      console.error('明日の必要材料取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodRequirements = async () => {
    try {
      const response = await fetch('/api/bento-inventory/period-requirements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: selectedDateRange.start,
          end_date: selectedDateRange.end
        })
      });
      const data = await response.json();
      setPeriodData(data);
    } catch (error) {
      console.error('期間別必要材料取得エラー:', error);
    }
  };

  const fetchPreparationSuggestions = async () => {
    try {
      const response = await fetch('/api/bento-inventory/preparation-suggestion');
      const data = await response.json();
      setPreparationSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('仕込み提案取得エラー:', error);
    }
  };

  const generateOrderList = async () => {
    try {
      const response = await fetch('/api/bento-inventory/generate-order-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          days_ahead: 3
        })
      });
      const data = await response.json();
      setOrderList(data.order_list || []);
      setActiveTab('order');
    } catch (error) {
      console.error('発注リスト生成エラー:', error);
    }
  };

  // 完成品在庫 functions
  const fetchDailyStock = async () => {
    setDailyStockLoading(true);
    setDailyStockError('');
    try {
      const response = await fetch('/api/bento-inventory/daily/status');
      const data = await response.json();
      if (data.success) {
        setDailyStockItems(data.items || []);
        setDailyStockDate(data.date || '');
      }
    } catch (error) {
      setDailyStockError('在庫情報の取得に失敗しました');
    } finally {
      setDailyStockLoading(false);
    }
  };

  const handleDailyStockSet = async () => {
    if (!setMenuId || !setLimitQty) {
      setDailyStockError('メニューIDと上限数を入力してください');
      return;
    }
    const limitVal = parseInt(setLimitQty, 10);
    if (isNaN(limitVal) || limitVal < 0) {
      setDailyStockError('上限数は0以上の整数を入力してください');
      return;
    }
    const body: { menuId: string; limitQty: number; remainingQty?: number } = {
      menuId: setMenuId,
      limitQty: limitVal,
    };
    if (setRemainingQty !== '') {
      const remVal = parseInt(setRemainingQty, 10);
      if (isNaN(remVal) || remVal < 0) {
        setDailyStockError('残数は0以上の整数を入力してください');
        return;
      }
      body.remainingQty = remVal;
    }

    setSetSubmitting(true);
    setDailyStockError('');
    try {
      const response = await fetch('/api/bento-inventory/daily/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        setDailyStockError(data.error || 'セットに失敗しました');
      } else {
        setSetMenuId('');
        setSetLimitQty('');
        setSetRemainingQty('');
        await fetchDailyStock();
      }
    } catch (error) {
      setDailyStockError('通信エラーが発生しました');
    } finally {
      setSetSubmitting(false);
    }
  };

  const handleDailyStockAdjust = async (menuId: string, delta: number) => {
    setAdjustingMenuId(menuId);
    setDailyStockError('');
    try {
      const response = await fetch('/api/bento-inventory/daily/adjust', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ menuId, delta }),
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = response.status === 409
          ? '在庫不足です（残数が0です）'
          : response.status === 400
            ? (data.error && data.error.includes('not set'))
              ? '今日の在庫がセットされていません。先に上のフォームでセットしてください'
              : '上限超過。先にセットで上限を増やしてください'
            : (data.error || '調整に失敗しました');
        await fetchDailyStock();
        setDailyStockError(errorMsg);
      } else {
        await fetchDailyStock();
      }
    } catch (error) {
      setDailyStockError('通信エラーが発生しました');
    } finally {
      setAdjustingMenuId(null);
    }
  };

  const handleBulkDailyStockSet = async (qty: number) => {
    if (qty < 0 || !Number.isInteger(qty)) {
      setDailyStockError('一括在庫数は0以上の整数を入力してください');
      return;
    }
    if (bentoMenus.length === 0) {
      setDailyStockError('弁当メニュー一覧の取得に失敗しています。再読み込みしてください');
      return;
    }
    const bulkTargetMenus = bentoMenus.filter((menu) => menu.isAvailable);
    if (bulkTargetMenus.length === 0) {
      setDailyStockError('公開中の弁当メニューがありません');
      return;
    }
    const confirmed = window.confirm(`本日の全弁当の残数を ${qty} にします。よろしいですか？\n※公開中メニューのみが対象です`);
    if (!confirmed) {
      return;
    }

    setBulkSetSubmitting(true);
    setDailyStockError('');
    let successCount = 0;

    try {
      for (const menu of bulkTargetMenus) {
        const response = await fetch('/api/bento-inventory/daily/set', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            menuId: String(menu.id),
            limitQty: qty,
            remainingQty: qty,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          setDailyStockError(`${successCount}件更新後に停止しました（${menu.name}）。${data.error || '一括セットに失敗しました'}`);
          return;
        }
        successCount += 1;
      }

      await fetchDailyStock();
    } catch (error) {
      setDailyStockError(`${successCount}件更新後に停止しました。通信エラーが発生しました`);
    } finally {
      setBulkSetSubmitting(false);
    }
  };

  const handleBulkDailyStockSetByInput = async () => {
    const valText = bulkSetQty.trim();
    if (!/^\d+$/.test(valText)) {
      setDailyStockError('一括在庫数は0以上の整数を入力してください');
      return;
    }
    await handleBulkDailyStockSet(parseInt(valText, 10));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sufficient':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'insufficient':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">読み込み中...</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-8 h-8 text-blue-600" />
            弁当予約×在庫連動システム
          </h1>
        </div>

        {/* タブナビゲーション */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => {
              setActiveTab('dailyStock');
              fetchDailyStock();
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'dailyStock'
                ? 'border-b-2 border-orange-600 text-orange-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            完成品在庫
          </button>
        </div>

        {/* 明日の必要材料タブ */}
        {activeTab === 'tomorrow' && tomorrowData && (
          <div>
            {/* サマリーカード */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">明日の予約数</p>
                    <p className="text-2xl font-bold text-blue-600">{tomorrowData.total_orders} 件</p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-400" />
                </div>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">在庫不足</p>
                    <p className="text-2xl font-bold text-red-600">{tomorrowData.insufficient_count} 品目</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">要注意</p>
                    <p className="text-2xl font-bold text-yellow-600">{tomorrowData.warning_count} 品目</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-yellow-400" />
                </div>
              </div>
            </div>

            {/* 予約詳細 */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">明日の予約内容</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tomorrowData.orders.map((order, index) => (
                    <div key={index} className="bg-white rounded p-3 shadow-sm">
                      <p className="font-medium">{order.menu_name}</p>
                      <p className="text-sm text-gray-600">
                        数量: {order.quantity} / 受取: {order.pickup_time}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 材料必要量テーブル */}
            <div>
              <h3 className="text-lg font-semibold mb-3">必要材料と在庫状況</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">状態</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">材料名</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">必要量</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">現在庫</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">過不足</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">メッセージ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tomorrowData.requirements.map((req) => (
                      <tr key={req.ingredient_id} className={
                        req.status === 'insufficient' ? 'bg-red-50' :
                        req.status === 'warning' ? 'bg-yellow-50' : ''
                      }>
                        <td className="px-4 py-2">{getStatusIcon(req.status)}</td>
                        <td className="px-4 py-2 font-medium">{req.name}</td>
                        <td className="px-4 py-2">{req.required_amount} {req.unit}</td>
                        <td className="px-4 py-2">{req.current_stock} {req.unit}</td>
                        <td className="px-4 py-2">
                          <span className={req.shortage > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
                            {req.shortage > 0 ? `-${req.shortage}` : '充足'} {req.shortage > 0 && req.unit}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">{req.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 期間別分析タブ */}
        {activeTab === 'period' && (
          <div>
            <div className="mb-6">
              <div className="flex gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
                  <input
                    type="date"
                    value={selectedDateRange.start}
                    onChange={(e) => setSelectedDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
                  <input
                    type="date"
                    value={selectedDateRange.end}
                    onChange={(e) => setSelectedDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <button
                  onClick={fetchPeriodRequirements}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  分析実行
                </button>
              </div>
            </div>

            {periodData && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">期間内予約数</p>
                    <p className="text-2xl font-bold text-blue-600">{periodData.total_orders} 件</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">不足見込み</p>
                    <p className="text-2xl font-bold text-red-600">{periodData.insufficient_count} 品目</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">要注意</p>
                    <p className="text-2xl font-bold text-yellow-600">{periodData.warning_count} 品目</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">材料名</th>
                        <th className="px-4 py-2 text-left">必要量合計</th>
                        <th className="px-4 py-2 text-left">現在庫</th>
                        <th className="px-4 py-2 text-left">過不足</th>
                        <th className="px-4 py-2 text-left">状態</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {periodData.requirements.map((req) => (
                        <tr key={req.ingredient_id}>
                          <td className="px-4 py-2 font-medium">{req.name}</td>
                          <td className="px-4 py-2">{req.required_amount} {req.unit}</td>
                          <td className="px-4 py-2">{req.current_stock} {req.unit}</td>
                          <td className="px-4 py-2">
                            <span className={req.shortage > 0 ? 'text-red-600' : 'text-green-600'}>
                              {req.shortage > 0 ? `-${req.shortage}` : '充足'} {req.shortage > 0 && req.unit}
                            </span>
                          </td>
                          <td className="px-4 py-2">{getStatusIcon(req.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 仕込み提案タブ */}
        {activeTab === 'preparation' && (
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <ChefHat className="w-6 h-6 text-orange-600" />
                本日の仕込み提案
              </h3>
              <p className="text-sm text-gray-600">明日の予約に基づいた最適な仕込み量と時間を提案します</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {preparationSuggestions.map((suggestion, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-lg">{suggestion.ingredient_name}</h4>
                    <span className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      {suggestion.prep_time}
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="text-2xl font-bold text-blue-600">{suggestion.quantity}</span>
                    <span className="text-gray-600 ml-1">{suggestion.unit}</span>
                  </div>
                  <p className="text-sm text-gray-700">{suggestion.suggestion}</p>
                </div>
              ))}
            </div>

            {preparationSuggestions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <ChefHat className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>現在、特別な仕込みは必要ありません</p>
              </div>
            )}
          </div>
        )}

        {/* 発注リストタブ */}
        {activeTab === 'order' && (
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-green-600" />
                自動生成発注リスト
              </h3>
              <p className="text-sm text-gray-600">今後3日間の予約と在庫状況から必要な発注を提案します</p>
            </div>

            {orderList.length > 0 ? (
              <div>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">優先度</th>
                        <th className="px-4 py-2 text-left">材料名</th>
                        <th className="px-4 py-2 text-left">発注量</th>
                        <th className="px-4 py-2 text-left">仕入先</th>
                        <th className="px-4 py-2 text-right">予想金額</th>
                        <th className="px-4 py-2 text-center">アクション</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {orderList.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getUrgencyColor(item.urgency)}`}>
                              {item.urgency === 'high' ? '高' : item.urgency === 'medium' ? '中' : '低'}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-medium">{item.ingredient_name}</td>
                          <td className="px-4 py-2">{item.order_quantity} {item.unit}</td>
                          <td className="px-4 py-2">{item.supplier}</td>
                          <td className="px-4 py-2 text-right">¥{item.estimated_cost.toLocaleString()}</td>
                          <td className="px-4 py-2 text-center">
                            <button className="text-blue-600 hover:text-blue-800 text-sm">
                              発注追加
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={4} className="px-4 py-2 text-right font-semibold">合計金額:</td>
                        <td className="px-4 py-2 text-right font-bold text-lg">
                          ¥{orderList.reduce((sum, item) => sum + item.estimated_cost, 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700">
                            一括発注
                          </button>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p className="text-gray-500">発注リストを生成するには「発注リスト生成」ボタンをクリックしてください</p>
              </div>
            )}
          </div>
        )}

        {/* 完成品在庫タブ */}
        {activeTab === 'dailyStock' && (
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Package className="w-6 h-6 text-orange-600" />
                完成品在庫管理（今日: {dailyStockDate || '---'}）
              </h3>
              <p className="text-sm text-gray-600">LINE予約弁当の完成品在庫を管理します</p>
            </div>

            {/* エラー表示 */}
            {dailyStockError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
                <XCircle className="w-5 h-5 flex-shrink-0" />
                {dailyStockError}
                <button onClick={() => setDailyStockError('')} className="ml-auto text-red-400 hover:text-red-600 text-sm">
                  閉じる
                </button>
              </div>
            )}

            {/* セットフォーム */}
            <div className="mb-6 bg-orange-50 rounded-lg p-4">
              <h4 className="font-medium mb-3">在庫セット</h4>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">弁当メニュー</label>
                  <select
                    value={setMenuId}
                    onChange={(e) => setSetMenuId(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg w-56"
                  >
                    <option value="">選択してください</option>
                    {bentoMenus.map((m) => (
                      <option key={m.id} value={String(m.id)}>
                        {m.isAvailable ? '✅' : '⛔'} {m.name}（¥{m.price}）{m.isAvailable ? '' : '【公開停止】'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">上限数</label>
                  <input
                    type="number"
                    min="0"
                    value={setLimitQty}
                    onChange={(e) => setSetLimitQty(e.target.value)}
                    placeholder="例: 10"
                    className="px-3 py-2 border border-gray-300 rounded-lg w-24"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">残数（空=上限と同じ）</label>
                  <input
                    type="number"
                    min="0"
                    value={setRemainingQty}
                    onChange={(e) => setSetRemainingQty(e.target.value)}
                    placeholder="空欄OK"
                    className="px-3 py-2 border border-gray-300 rounded-lg w-28"
                  />
                </div>
                <button
                  onClick={handleDailyStockSet}
                  disabled={setSubmitting || bulkSetSubmitting}
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {setSubmitting ? '処理中...' : 'セット'}
                </button>
              </div>
              <div className="mt-4 border-t border-orange-200 pt-4">
                <h5 className="text-sm font-medium text-gray-700 mb-2">一括在庫数セット（全弁当）</h5>
                <div className="flex flex-wrap gap-2 items-end">
                  <button
                    onClick={() => handleBulkDailyStockSet(0)}
                    disabled={setSubmitting || bulkSetSubmitting}
                    className="bg-gray-700 text-white px-3 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                  >
                    {bulkSetSubmitting ? '処理中...' : '一括0'}
                  </button>
                  <button
                    onClick={() => handleBulkDailyStockSet(5)}
                    disabled={setSubmitting || bulkSetSubmitting}
                    className="bg-gray-700 text-white px-3 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                  >
                    {bulkSetSubmitting ? '処理中...' : '一括5'}
                  </button>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={bulkSetQty}
                    onChange={(e) => setBulkSetQty(e.target.value)}
                    placeholder="在庫数"
                    className="px-3 py-2 border border-gray-300 rounded-lg w-24"
                  />
                  <button
                    onClick={handleBulkDailyStockSetByInput}
                    disabled={setSubmitting || bulkSetSubmitting}
                    className="bg-orange-700 text-white px-3 py-2 rounded-lg hover:bg-orange-800 disabled:opacity-50"
                  >
                    {bulkSetSubmitting ? '処理中...' : '一括セット'}
                  </button>
                </div>
              </div>
            </div>

            {/* 在庫テーブル */}
            {dailyStockLoading ? (
              <div className="text-center py-8 text-gray-500">読み込み中...</div>
            ) : dailyStockItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">メニュー</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">上限数</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">残数</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">更新日時</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">調整</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dailyStockItems.map((item) => (
                      <tr key={item.menuId} className={item.remainingQty === 0 ? 'bg-red-50' : ''}>
                        <td className="px-4 py-2 font-medium">
                          {(() => {
                            const m = bentoMenus.find(b => String(b.id) === item.menuId);
                            const name = m ? m.name : item.menuId;
                            const badge = m
                              ? m.isAvailable
                                ? <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-normal">公開中</span>
                                : <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-200 text-gray-500 font-normal">公開停止</span>
                              : null;
                            return <>{name}{badge}</>;
                          })()}
                        </td>
                        <td className="px-4 py-2">{item.limitQty}</td>
                        <td className="px-4 py-2">
                          <span className={`font-bold ${item.remainingQty === 0 ? 'text-red-600' : item.remainingQty <= 3 ? 'text-yellow-600' : 'text-green-600'}`}>
                            {item.remainingQty}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">{item.updatedAt || '-'}</td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDailyStockAdjust(item.menuId, -1)}
                              disabled={adjustingMenuId === item.menuId}
                              className="bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 disabled:opacity-50 font-bold"
                            >
                              -1
                            </button>
                            <button
                              onClick={() => handleDailyStockAdjust(item.menuId, 1)}
                              disabled={adjustingMenuId === item.menuId}
                              className="bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200 disabled:opacity-50 font-bold"
                            >
                              +1
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>今日の完成品在庫はまだセットされていません</p>
              </div>
            )}
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default BentoInventoryIntegration;
